/**
 * Where a ground antenna's dish points during a dive. pointingFor gives the
 * look angles from the antenna to a mission at a time, from the mission's TLE
 * through the orbit engine (parseTLE, getPositionEcf, checkVisibility).
 * resolveDivePointing picks what to point at, in this order:
 *
 * 1. 'contact': the antenna's live contacts (useLoader.activeContacts), the
 *    one highest in the sky now. The stored contact elevation is the value at
 *    AOS, so the ranking uses each mission's current elevation instead.
 * 2. 'scheduled': a contact window of the analysis that is in progress
 *    without a live contact (after a clock jump resets the live contacts, or
 *    where the live tick and the scan disagree near the mask): the
 *    satellite's current look angles.
 * 3. 'next-window': the earliest upcoming window on the antenna across the
 *    analysis results, with the dish waiting at the pass's rise azimuth at
 *    the antenna's minimum elevation.
 * 4. 'stow': azimuth 0, elevation 90.
 *
 * Missions without a usable TLE (L1, deep space, synthetic windows) are
 * skipped at every step because their look angles cannot be computed.
 */
import { ANALYSIS } from '@/features/loader/constants';
import { checkVisibility, getPositionEcf, parseTLE } from '@/features/loader/engine';
import type { ActiveContact } from '@/features/loader/store';
import type { Antenna, ContactWindowMap, Mission, Tle } from '@/features/loader/types';

export interface LookAngles {
  /** Degrees clockwise from true north, 0 to 360. */
  azimuthDeg: number;
  /** Degrees above the horizon; negative below it. */
  elevationDeg: number;
}

/** Zenith stow: where the dish rests with nothing to track. */
export const STOW: Readonly<LookAngles> = Object.freeze({ azimuthDeg: 0, elevationDeg: 90 });

export type PointingSource = 'contact' | 'scheduled' | 'pass-preview' | 'next-window' | 'stow';

export interface DivePointing extends LookAngles {
  source: PointingSource;
  missionId: string | null;
  /** Start of the window followed ('scheduled') or waited for ('next-window'). */
  windowStartMs: number | null;
}

export interface PointingContext {
  inspectedPass?: { missionId: string; antennaId: string; startMs: number; endMs: number } | null;
  missions: Mission[];
  activeContacts: ActiveContact[];
  /** results.contactWindows of the last analysis (null before the first run). */
  contactWindows: ContactWindowMap | null;
}

/** orbit.tle, or the legacy single-string TLE split into its element lines (as globe/missions.ts tleOf reads it). */
export function missionTle(mission: Mission): Tle | null {
  const tle = mission.orbit?.tle;
  if (tle && typeof tle.line1 === 'string' && typeof tle.line2 === 'string') return tle;
  if (typeof mission.tle === 'string') {
    const lines = mission.tle
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    const line1 = lines.find((l) => l.startsWith('1 '));
    const line2 = lines.find((l) => l.startsWith('2 '));
    if (line1 && line2) return { line1, line2 };
  }
  return null;
}

/** The antenna's minimum tracking elevation (coverage.elevationMinDeg, else the analysis default). */
export function minElevationDeg(antenna: Antenna): number {
  return antenna.coverage?.elevationMinDeg ?? ANALYSIS.MIN_ELEVATION_DEG;
}

/** Look angles from the antenna to the mission at timeMs; null without a usable TLE or when SGP4 fails. */
export function pointingFor(antenna: Antenna, mission: Mission, timeMs: number): LookAngles | null {
  const tle = missionTle(mission);
  if (!tle) return null;
  const satrec = parseTLE(tle);
  if (!satrec) return null;
  const ecf = getPositionEcf(satrec, timeMs);
  if (!ecf || ![ecf.x, ecf.y, ecf.z].every(Number.isFinite)) return null;
  const look = checkVisibility(
    ecf,
    { lat: antenna.location.lat, lon: antenna.location.lon },
    minElevationDeg(antenna),
  );
  if (!Number.isFinite(look.azimuth) || !Number.isFinite(look.elevation)) return null;
  return { azimuthDeg: ((look.azimuth % 360) + 360) % 360, elevationDeg: look.elevation };
}

export function resolveDivePointing(antenna: Antenna, ctx: PointingContext, timeMs: number): DivePointing {
  const byId = new Map(ctx.missions.map((m) => [m.id, m]));
  const inspected = ctx.inspectedPass;
  if (inspected?.antennaId === antenna.id) {
    const mission = byId.get(inspected.missionId);
    const look = mission ? pointingFor(antenna, mission, Math.max(timeMs, inspected.startMs)) : null;
    if (look && timeMs < inspected.endMs) {
      const waiting = timeMs < inspected.startMs;
      if (waiting || look.elevationDeg >= minElevationDeg(antenna))
        return {
          azimuthDeg: look.azimuthDeg,
          elevationDeg: waiting ? minElevationDeg(antenna) : look.elevationDeg,
          source: waiting ? 'next-window' : 'pass-preview',
          missionId: inspected.missionId,
          windowStartMs: inspected.startMs,
        };
    }
    // The selected pass has ended or is unavailable; do not silently point at another mission.
    return { ...STOW, source: 'stow', missionId: inspected.missionId, windowStartMs: inspected.startMs };
  }

  // 1. Live contacts on this antenna, highest in the sky now.
  let best: { missionId: string; look: LookAngles } | null = null;
  for (const c of ctx.activeContacts) {
    if (c.antennaId !== antenna.id) continue;
    const mission = byId.get(c.missionId);
    const look = mission ? pointingFor(antenna, mission, timeMs) : null;
    if (!look || look.elevationDeg <= 0) continue;
    if (!best || look.elevationDeg > best.look.elevationDeg) best = { missionId: c.missionId, look };
  }
  if (best) return { ...best.look, source: 'contact', missionId: best.missionId, windowStartMs: null };

  // 2 and 3. Scheduled windows on this antenna that have not ended, earliest start first.
  const windows: { missionId: string; startMs: number; endMs: number }[] = [];
  for (const [missionId, byAntenna] of Object.entries(ctx.contactWindows ?? {})) {
    for (const w of byAntenna[antenna.id] ?? []) {
      if (w.endMs > timeMs && !w.synthetic) windows.push({ missionId, startMs: w.startMs, endMs: w.endMs });
    }
  }
  windows.sort((a, b) => a.startMs - b.startMs);
  const minEl = minElevationDeg(antenna);
  for (const w of windows) {
    const mission = byId.get(w.missionId);
    if (!mission) continue;
    if (w.startMs <= timeMs) {
      const look = pointingFor(antenna, mission, timeMs);
      if (!look || look.elevationDeg <= 0) continue;
      return {
        ...look,
        elevationDeg: Math.max(minEl, look.elevationDeg),
        source: 'scheduled',
        missionId: w.missionId,
        windowStartMs: w.startMs,
      };
    }
    const rise = pointingFor(antenna, mission, w.startMs);
    if (!rise) continue;
    return {
      azimuthDeg: rise.azimuthDeg,
      elevationDeg: minEl,
      source: 'next-window',
      missionId: w.missionId,
      windowStartMs: w.startMs,
    };
  }

  // 4. Nothing to track.
  return { ...STOW, source: 'stow', missionId: null, windowStartMs: null };
}
